import { useState } from "react";
import { MedicineDto, medecineIdKey } from "../types/medicine";
import { medicineService } from "../services/medicineService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useMedicines = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: medicines = [],
    loading: isFetching,
    error: getError,
    refetch: fetchMedicines,
    invalidateCache,
  } = useApi<MedicineDto[]>("medicines", (signal) => medicineService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newMedicine: Record<string, any>) => medicineService.create(newMedicine as MedicineDto));

  // 3. Το UPDATE τυλίγεται στο καθαρό useMutation (προαιρετικό αλλά χρήσιμο)
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number; data: Record<string, any> }) => 
    medicineService.update(id, data as MedicineDto)
  );

  // 4. Το DELETE τυλίγεται στο καθαρό useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => medicineService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createMedicine = async (newMedicine: Record<string, any>): Promise<void> => {
    try {
      await createExecution(newMedicine);
      
      // Καθαρισμός cache για να φέρει τα νέα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το φάρμακο αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchMedicines(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateMedicine = async (id: number, updatedData: Record<string, any>): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το φάρμακο ενημερώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchMedicines(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteMedicines = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρισμός cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchMedicines(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    medicines,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchMedicines: () => fetchMedicines(true),
    createMedicine,
    updateMedicine,
    deleteMedicines,
    modalState,
    closeModal,
  };
};